import React from 'react';
import { Paper, ScrollArea, Stack, Table, Text, Checkbox, Badge, Loader, Center, Box, Group, Tooltip } from '@mantine/core';
import { IconFileInvoice, IconLock } from '@tabler/icons-react';
import { inject, observer } from 'mobx-react';
import { useElementSize } from '@mantine/hooks';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import { BillingReport } from '../../../global/entities/TestKitOrder/BillingTypes';
import { useTranslation } from 'react-i18next';

/**
 * BillingOrdersTable - Orders table for billing management
 * Part of BL-002 Admin Billing Reports Feature - Step 25
 */
interface BillingOrdersTableProps {
    onOrdersSelectionChange?: (selectedOrders: string[]) => void;
    isLoading?: boolean;
    billingData?: BillingReport | null;
}

const BillingOrdersTableContent: React.FC<BillingOrdersTableProps> = inject()(
    observer(({ onOrdersSelectionChange, isLoading = false, billingData = null }) => {
        const { t } = useTranslation();
        const { ref, width } = useElementSize();
        const { setSorting, sortBy, reverseSortDirection, setSortedData, sortedData } = useTable();
        const [selectedOrders, setSelectedOrders] = React.useState<string[]>([]);

        React.useEffect(() => {
            // Initialize with billing data orders
            if (billingData?.orders) 
                setSortedData(billingData.orders);
             else 
                setSortedData([]);
            
        }, [setSortedData, billingData]);

        // Automatically unselect orders that are no longer selectable (e.g., became batched)
        React.useEffect(() => {
            if (billingData?.orders && selectedOrders.length > 0) {
                const currentSelectableOrderIds = new Set(
                    billingData.orders
                        .filter(order => isOrderSelectable(order))
                        .map(order => order.orderID.toString())
                );
                
                const stillSelectableOrders = selectedOrders.filter(orderId => 
                    currentSelectableOrderIds.has(orderId)
                );
                
                // If some orders are no longer selectable, update selection
                if (stillSelectableOrders.length !== selectedOrders.length) {
                    setSelectedOrders(stillSelectableOrders);
                    onOrdersSelectionChange?.(stillSelectableOrders);
                }
            }
        }, [billingData?.orders, selectedOrders, onOrdersSelectionChange]);

        const handleSelectAll = (checked: boolean) => {
            if (checked) {
                // Only select orders that are selectable (not batched and not billed)
                const selectableOrderIds = billingData?.orders?.filter(order => isOrderSelectable(order))
                    .map(order => order.orderID.toString()) || [];
                setSelectedOrders(selectableOrderIds);
                onOrdersSelectionChange?.(selectableOrderIds);
            } else {
                setSelectedOrders([]);
                onOrdersSelectionChange?.([]);
            }
        };

        const handleSelectOrder = (orderId: string, checked: boolean) => {
            let newSelection: string[];
            if (checked) 
                newSelection = [...selectedOrders, orderId];
             else 
                newSelection = selectedOrders.filter(id => id !== orderId);
            
            setSelectedOrders(newSelection);
            onOrdersSelectionChange?.(newSelection);
        };
        
        // Step 45: Helper to check if order can be selected (not in a batch)
        const isOrderSelectable = (order: any) => {
            return !order.isInBatch && !order.isBilled;
        };

        // Calculate based on selectable orders only
        const selectableOrders = billingData?.orders?.filter(order => isOrderSelectable(order)) || [];
        const totalSelectableOrders = selectableOrders.length;
        const allSelected = selectedOrders.length === totalSelectableOrders && totalSelectableOrders > 0;
        const indeterminate = selectedOrders.length > 0 && selectedOrders.length < totalSelectableOrders;

        return (
            <Paper w="100%" h={'min-content'} ref={ref}>
                <Stack gap={'md'} h={'100%'}>
                    <ScrollArea w="100%">
                        <Table verticalSpacing="xs">
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th w={40}>
                                        <Checkbox
                                            checked={allSelected}
                                            indeterminate={indeterminate}
                                            disabled={totalSelectableOrders === 0}
                                            onChange={(event) => handleSelectAll(event.currentTarget.checked)}
                                        />
                                    </Table.Th>
                                    <Th
                                        sorted={sortBy === 'orderReference'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('orderReference', billingData?.orders || [])}
                                        width={120}
                                    >
                                        {t('billing.table.orderNumber')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'healthcareFacilityName'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('healthcareFacilityName', billingData?.orders || [])}
                                        width={150}
                                    >
                                        {t('billing.table.facility')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'contactName'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('contactName', billingData?.orders || [])}
                                        width={130}
                                    >
                                        {t('billing.table.contactName')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'contactEmail'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('contactEmail', billingData?.orders || [])}
                                        width={130}
                                    >
                                        {t('billing.table.contactEmail')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'testKitType'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('testKitType', billingData?.orders || [])}
                                        width={120}
                                    >
                                        {t('billing.table.testKit')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'orderDate'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('orderDate', billingData?.orders || [])}
                                        width={100}
                                    >
                                        {t('billing.table.orderDate')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'billedDateTime'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('billedDateTime', billingData?.orders || [])}
                                        width={100}
                                    >
                                        {t('billing.table.billedDate')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'totalAmount'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('totalAmount', billingData?.orders || [])}
                                        width={80}
                                    >
                                        {t('billing.table.amount')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'orderStatusName'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('orderStatusName', billingData?.orders || [])}
                                        width={100}
                                    >
                                        {t('billing.table.status')}
                                    </Th>
                                    <Table.Th w={120}>
                                        {t('billing.table.batch')}
                                    </Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {isLoading ? (
                                    <Table.Tr>
                                        <Table.Td colSpan={11}>
                                            <Center py="xl">
                                                <Loader size="md" />
                                            </Center>
                                        </Table.Td>
                                    </Table.Tr>
                                ) : sortedData && sortedData.length === 0 ? (
                                    <Table.Tr>
                                        <Table.Td colSpan={11}>
                                            <Center py="xl">
                                                <Box ta="center">
                                                    <IconFileInvoice size={48} color="var(--mantine-color-gray-4)" />
                                                    <Text size="lg" fw={500} c="dimmed" mt="md">
                                                        {t('billing.table.noOrders')}
                                                    </Text>
                                                    <Text size="sm" c="dimmed" mt="xs">
                                                        {t('billing.table.noOrdersHint')}
                                                    </Text>
                                                </Box>
                                            </Center>
                                        </Table.Td>
                                    </Table.Tr>
                                ) : (
                                    sortedData?.map((order, index: number) => (
                                    <Table.Tr
                                        key={order.orderID}
                                        style={{
                                            background:
                                                index % 2 === 1
                                                    ? undefined
                                                    : 'var(--Background-Gray-light, #E9ECEF)',
                                        }}
                                    >
                                        <Table.Td>
                                            <Checkbox
                                                checked={selectedOrders.includes(order.orderID.toString())}
                                                disabled={!isOrderSelectable(order)}
                                                onChange={(event) => 
                                                    handleSelectOrder(order.orderID.toString(), event.currentTarget.checked)
                                                }
                                            />
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="sm" fw={500}>
                                                {order.orderReference}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="sm" truncate maw={150}>
                                                {order.healthcareFacilityName}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="sm" truncate maw={130}>
                                                {order.contactName}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="sm" truncate maw={130}>
                                                {order.contactEmail}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="sm" truncate maw={120}>
                                                Test Kit
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="sm">
                                                {order.formattedOrderDate}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="sm">
                                                {order.isBilled ? order.formattedBilledDateTime : '-'}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Text size="sm" fw={500}>
                                                {order.formattedTotalAmount}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            <Badge
                                                color={order.isBilled ? 'green' : 'orange'}
                                                variant="light"
                                                size="sm"
                                            >
                                                {order.orderStatusName}
                                            </Badge>
                                        </Table.Td>
                                        <Table.Td>
                                            {order.isInBatch ? (
                                                <Group gap="xs">
                                                    <Tooltip label={t('billing.table.batchLocked')}>
                                                        <IconLock size={14} color="var(--mantine-color-red-6)" />
                                                    </Tooltip>
                                                </Group>
                                            ) : (
                                                <Text size="sm" c="dimmed">-</Text>
                                            )}
                                        </Table.Td>
                                    </Table.Tr>
                                    ))
                                )}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea>
                </Stack>
            </Paper>
        );
    })
);

const BillingOrdersTable: React.FC<BillingOrdersTableProps> = (props) => {
    return (
        <TableContextProvider>
            <BillingOrdersTableContent {...props} />
        </TableContextProvider>
    );
};

export default BillingOrdersTable; 